import {GitHubAvatar} from '@github-ui/github-avatar'
import {userHovercardPath} from '@github-ui/paths'
import {KebabHorizontalIcon, LinkIcon, PencilIcon, TrashIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, Label, Link, RelativeTime, useConfirm} from '@primer/react'
import type {ReactNode} from 'react'
import {useCallback, useRef} from 'react'

import type {MemexStatus} from '../../../api/memex/contracts'
import {StatusUpdateDestroy} from '../../../api/stats/contracts'
import {getInitialState} from '../../../helpers/initial-state'
import {ViewerPrivileges} from '../../../helpers/viewer-privileges'
import {usePostStats} from '../../../hooks/common/use-post-stats'
import {useStatusUpdates} from '../../../state-providers/status-updates/status-updates-context'
import {StatusUpdatesResources} from '../../../strings'
import styles from './header.module.css'

type ItemOptionsProps = {
  handleOnEditClick: () => void
  status: MemexStatus
}

const CreatorLink = ({creator, children}: {creator: MemexStatus['creator']; children: ReactNode}) => {
  return (
    <Link
      href={`/${creator.login}`}
      data-hovercard-url={userHovercardPath({owner: creator.login})}
      className={styles.creatorLink}
    >
      {children}
    </Link>
  )
}

const ItemOptions = ({status, handleOnEditClick}: ItemOptionsProps) => {
  const {deleteStatusUpdate} = useStatusUpdates()
  const {hasWritePermissions} = ViewerPrivileges()
  const contentRef = useRef<HTMLButtonElement>(null)
  const confirm = useConfirm()
  const {postStats} = usePostStats()
  const {loggedInUser} = getInitialState()

  const onDeleteStatus = useCallback(async () => {
    if (!hasWritePermissions) {
      return
    }

    if (
      await confirm({
        title: StatusUpdatesResources.confirmDeleteTitle,
        content: StatusUpdatesResources.confirmDeleteBody,
        confirmButtonType: 'danger',
        confirmButtonContent: 'Delete',
      })
    ) {
      deleteStatusUpdate(status.id)
      postStats({
        name: StatusUpdateDestroy,
        context: JSON.stringify({
          id: status.id,
        }),
      })
    }
  }, [confirm, deleteStatusUpdate, hasWritePermissions, postStats, status.id])

  const onCopyLink = useCallback(() => {
    try {
      const {origin, pathname} = window.location
      const url = `${origin}${pathname}?pane=info&statusUpdateId=${status.id}`
      navigator.clipboard.writeText(url)
    } catch {
      // ignore error when user did not grant clipboard access
    }
  }, [status.id])

  return (
    <div className={styles.actions}>
      <ActionMenu anchorRef={contentRef}>
        <ActionMenu.Anchor>
          <IconButton variant="invisible" aria-label="Status update options" icon={KebabHorizontalIcon} size="medium" />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            <ActionList.Item onSelect={onCopyLink}>
              <ActionList.LeadingVisual>
                <LinkIcon />
              </ActionList.LeadingVisual>
              Copy link
            </ActionList.Item>
            {hasWritePermissions &&
              (!loggedInUser?.isSpammy || (loggedInUser?.isSpammy && status.creator.login === loggedInUser?.login)) && (
                <>
                  <ActionList.Item onSelect={handleOnEditClick}>
                    <ActionList.LeadingVisual>
                      <PencilIcon />
                    </ActionList.LeadingVisual>
                    Edit
                  </ActionList.Item>
                  <ActionList.Item variant="danger" onSelect={onDeleteStatus}>
                    <ActionList.LeadingVisual>
                      <TrashIcon />
                    </ActionList.LeadingVisual>
                    Delete
                  </ActionList.Item>
                </>
              )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    </div>
  )
}

export const StatusUpdateItemHeader = ({status, handleOnEditClick}: ItemOptionsProps) => {
  const {creator, updatedAt} = status
  const {loggedInUser} = getInitialState()

  return (
    <div className={styles.header}>
      <div className={styles.content}>
        <div className={styles.avatarContainer}>
          <CreatorLink creator={creator}>
            <GitHubAvatar src={creator.avatarUrl} size={24} alt={`@${creator.login}`} className={styles.avatar} />
          </CreatorLink>
        </div>
        <h3 className={styles.creatorInfo}>
          <CreatorLink creator={creator}>{creator.login}</CreatorLink>
          {status.userHidden && loggedInUser?.login !== creator.login && <Label variant="danger">Spammy</Label>}
          <div className={styles.timestampArea}>
            <span className={styles.updatedText}>updated</span>
            <Link href={`?pane=info&statusUpdateId=${status.id}`} className={styles.timestampLink}>
              <RelativeTime datetime={updatedAt} className={styles.timestamp} />
            </Link>
          </div>
        </h3>
      </div>
      <ItemOptions status={status} handleOnEditClick={handleOnEditClick} />
    </div>
  )
}
